import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import * as Act from './audits.actions';
import { Observable, of } from "rxjs";
import { endpoints } from "../../constants/endpoints";
import { AuditStatusesEnum, AuditTypesEnum, IAudit, IAuditDetail } from "../../interfaces/IAudit";
import { catchError, map, tap } from "rxjs/operators";
import { IValidatedAuditNdc } from "../../interfaces/IValidatedAuditNdc";
import { IResponse } from "../../interfaces/IResponse";
import { deepCopy } from "../../helpers/helpers";
import { IConfirmedDetailModel } from "src/app/audits/interfaces/confirmed-audit-detail.interface";
import { IUnconfirmedNdcInBackCount } from "src/app/inventory/interfaces/unconfirmed-ndc-in-back-count.interface";

export interface AuditsStateModel {
    loading: boolean;
    success: boolean;
    audits: IAudit[];
    auditWithDetails: IAudit;
    lastAddedAuditDetailId: number;
    createAuditNoDrugsResponse: boolean;
    createAuditDetailDrugIsAlreadyAddedResponse: boolean;
    recentlyConfirmedDetails: IConfirmedDetailModel[];
    validatedAuditDetails: IValidatedAuditNdc[];
    response: IResponse;
    filtersForm: any;
    backCountsCount: number;
    firstBackCountID: string;
    bulkConfirmNDCsInventoryChanged: boolean;
    currentUnconfirmedNdcsInBackCount: IUnconfirmedNdcInBackCount[];
}

@State<AuditsStateModel>({
    name: 'audits',
})

@Injectable()
export class AuditsState {

    /**
     *
     */
    constructor(
        private http: HttpClient,
        private store: Store
    ) {  
    }

    @Selector()
    static loading(state: AuditsStateModel): boolean {
      return state.loading;
    }

    @Selector()
    static success(state: AuditsStateModel): boolean {
      return state.success;
    }
  
    @Selector()
    static audits(state: AuditsStateModel): IAudit[] {
      return state.audits;
    }

    @Selector()
    static auditWithDetails(state: AuditsStateModel): IAudit {
      return state.auditWithDetails;
    }

    @Selector()
    static response(state: AuditsStateModel): IResponse {
      return state.response;
    }

    @Selector()
    static filtersForm(state: AuditsStateModel): any {
      return state.filtersForm;
    }

    @Selector()
    static backCountsCount (state: AuditsStateModel): number {
      return state.backCountsCount;
    }

    @Selector()
    static firstBackCountID (state: AuditsStateModel): string {
      return state.firstBackCountID;
    }

    @Selector()
    static bulkConfirmNDCsInventoryChanged (state: AuditsStateModel): boolean {
      return state.bulkConfirmNDCsInventoryChanged;
    }

    @Selector()
    static currentUnconfirmedNdcsInBackCount (state: AuditsStateModel): IUnconfirmedNdcInBackCount[] {
      return state.currentUnconfirmedNdcsInBackCount;
    }

    @Action(Act.SaveAuditsFilters)
    saveAuditsFilters(ctx: StateContext<AuditsStateModel>, {filtersForm}: Act.SaveAuditsFilters): void {
      ctx.patchState({filtersForm});
    }

    @Action(Act.GetAudits)
    getAudits(ctx: StateContext<AuditsStateModel>, {criteria}: Act.GetAudits): Observable<AuditsStateModel> {
      ctx.patchState({loading: true});
      let params = new HttpParams();
      if (criteria.searchValue) {
        params = params.append('searchValue', criteria.searchValue);
      }
  
      if (criteria.auditTypes && criteria.auditTypes.length > 0) {
        criteria.auditTypes.forEach(type => {
          params = params.append('auditTypes', type);
        });
      }
      return this.http.get(endpoints.audit.getAudits, {params})
        .pipe(
          tap((resp: any) => {
            ctx.patchState({
              audits: resp.value || [],
              loading: false,
            });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.CreateAudit)
    createAudit(ctx: StateContext<AuditsStateModel>, {payload}: Act.CreateAudit): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, auditWithDetails: null, success: null, createAuditNoDrugsResponse: false});
      return this.http.put(endpoints.audit.createAudit, payload)
        .pipe(
          tap((resp: any) => {
            if(resp?.success && !resp?.value?.isNoDrugs) {
              ctx.patchState({
                success: true,
                auditWithDetails: {id: resp?.value?.auditId}
              });
            } 
            else if (resp?.success && resp?.value?.isNoDrugs) {
              ctx.patchState({
                success: true,
                createAuditNoDrugsResponse: true
              });
            }
            ctx.patchState({
              loading: false
            });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetAuditWithDetails)
    getAuditWithDetails(ctx: StateContext<AuditsStateModel>, {id}: Act.GetAuditWithDetails): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, auditWithDetails: null, response: null});
      return this.http.get(endpoints.audit.getAuditWithDetails + `/${id}`)
        .pipe(
          tap((resp: any) => {
            ctx.patchState({
              loading: false,
              response: resp
            });
            if(resp?.success) {
              ctx.patchState({
                success: true,
                auditWithDetails: resp?.value?.audit
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.DeleteAudit)
    deleteAudit(ctx: StateContext<AuditsStateModel>, {id}: Act.DeleteAudit): Observable<AuditsStateModel> {
      ctx.patchState({loading: true});
      return this.http.delete(endpoints.audit.getAuditWithDetails +`/${id}`)
        .pipe(
          tap((resp: any) => {
            ctx.patchState({
              loading: false
            });
            if(resp?.success) {
              ctx.patchState({
                success: true,
                audits: ctx.getState().audits.filter(x => x.id !== id)
              });
            }
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.AddNewNdc)
    addNewNdc(ctx: StateContext<AuditsStateModel>, {payload}: Act.AddNewNdc): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, lastAddedAuditDetailId: null, success: null, createAuditDetailDrugIsAlreadyAddedResponse: false});
      return this.http.put(endpoints.audit.addNewDetail, payload)
        .pipe(
          tap((resp: any) => {
            if(resp?.success) {
              if(!resp?.value?.drugIsAlreadyInAudit) {
                const copy = {...ctx.getState()?.auditWithDetails};
                copy.type = AuditTypesEnum.AdHoc;
                copy.from = null;
                copy.to = null;
                const toAdd = {
                  id: resp?.value?.createdDetailId,
                  drugId: payload?.drugId,
                  gpi: payload.gpi,
                  packageNDC11Digit: payload?.ndc,
                  friendlyName: payload?.friendlyName,
                  onHand: payload?.onHand,
                  similarOnHand: payload.similarOnHand,
                  inventoryLocationID: payload?.locationId,
                  inventoryLocationName: payload?.inventoryLocationName,
                  adjustments: [],
                  notes: [],
                  pendingTransactions: resp?.value?.pendingTransactions || []
                };

                copy.details = [...copy.details, toAdd];
                ctx.patchState({
                  success: true,
                  lastAddedAuditDetailId: resp?.value?.createdDetailId,
                  auditWithDetails: copy
                });
              } else {
                ctx.patchState({
                  createAuditDetailDrugIsAlreadyAddedResponse: true,
                  success: true,
                });
              } 
            }
            ctx.patchState({
              loading: false,
            });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.RemoveNdc)
    removeNdc(ctx: StateContext<AuditsStateModel>, {id}: Act.RemoveNdc): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, success: null});
      return this.http.delete(endpoints.audit.removeDetail + '/' + id)
        .pipe(
          tap((resp: any) => {
            if(resp?.success) {
              const copy = {...ctx.getState()?.auditWithDetails};
              copy.type = AuditTypesEnum.AdHoc;
              copy.from = null;
              copy.to = null;
              copy.details = ctx.getState()?.auditWithDetails?.details?.filter(x => x?.id.toString() !== id);
              ctx.patchState({
                success: true,
                auditWithDetails: copy
              });
            }
            ctx.patchState({
              loading: false, success : resp?.success
            });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.ConfirmAuditDetail)
    confirmAuditDetail(ctx: StateContext<AuditsStateModel>, {payload}: Act.ConfirmAuditDetail): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, success: null, bulkConfirmNDCsInventoryChanged: null, validatedAuditDetails: []});
      return this.http.patch(endpoints.audit.confirmAuditDetails, payload)
        .pipe(
          tap((resp: any) => {
              const details = ctx.getState()?.auditWithDetails?.details?.map(
                x => 
                 {
                  const confirmedDetail = ((resp?.value?.confirmedDetails as any[])?.find(confirmed => confirmed.id === x.id));
                  x = confirmedDetail ?
                  {...x, 
                    confirmByFirstName: confirmedDetail?.confirmedByFirstName, confirmByLastName: confirmedDetail?.confirmedByLastName, 
                    confirmationDate: confirmedDetail?.confirmationDate, initialInventoryCount: confirmedDetail?.initialInventoryCount,
                    adjustments: confirmedDetail?.transactions, notes: confirmedDetail?.notes, actualCount: confirmedDetail?.actualCount
                  } : x; 
                  return x;
                });

                const copy = {...ctx.getState()?.auditWithDetails};
                copy.details = details;

              ctx.patchState({
                success: resp?.success,
                auditWithDetails: copy,
                loading: false,
                recentlyConfirmedDetails: resp?.value?.confirmedDetails,
                bulkConfirmNDCsInventoryChanged: resp?.value?.bulkConfirmNDCsInventoryChanged,
                validatedAuditDetails: resp?.value?.inventoryChangedDetails
              });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.CompleteAudit)
    completeAudit(ctx: StateContext<AuditsStateModel>, {payload}: Act.CompleteAudit): Observable<AuditsStateModel> {
      ctx.patchState({loading: true, success: null});
      return this.http.patch(endpoints.audit.completeAudit, payload)
        .pipe(
          tap((resp: any) => {
            if(resp?.success && resp?.value?.isCompleted) {
              const copy = {...ctx.getState().auditWithDetails};
              copy.status = AuditStatusesEnum.Completed;
              ctx.patchState({
                success: true,
                auditWithDetails: copy
              });
            } 
            else if (resp?.success && resp?.value?.isCompleted === false) { 
              const copy = {...ctx.getState().auditWithDetails};
              copy.details = [...resp?.value?.auditNewDetails, ...copy.details];
              ctx.patchState({
                success: false,
                auditWithDetails: copy
              });
            }
            ctx.patchState({
              loading: false
            });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.SetAuditsList)
    setAuditsList(ctx: StateContext<AuditsStateModel>, {audits}: Act.SetAuditsList): void {
      ctx.patchState({audits: audits || []});
    }

    @Action(Act.ValidateDetails)
    getInventoriesOnHand(ctx: StateContext<AuditsStateModel>, {payload}: Act.ValidateDetails): Observable<any[]> {
      ctx.patchState({loading: true, success: false});
      return this.http.post(endpoints.audit.validateDetails, payload)
        .pipe(
          tap((resp: any) => {
            if (resp.success) {
              ctx.patchState({validatedAuditDetails : resp?.value, success: true});
            }
            ctx.patchState({loading: false});
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.RemovePendingTransactionFromAuditDetail)
    removePendingTransactionFromAuditDetail(ctx: StateContext<AuditsStateModel>, {drugId, pendingTransactionId}: Act.RemovePendingTransactionFromAuditDetail): void {
      const auditCopy = deepCopy(ctx.getState().auditWithDetails);
      const auditDetails = auditCopy.details.filter(x => x.drugId === drugId);
      auditDetails.forEach(auditDetail => {
        const pendingTransactions = auditDetail?.pendingTransactions?.filter(x => x.id !== pendingTransactionId);
        auditDetail.pendingTransactions = pendingTransactions;
        auditCopy.details = auditCopy.details.map(x => x.drugId === drugId && (!auditDetail.inventoryLocationID || x.inventoryLocationID === auditDetail.inventoryLocationID) ? 
        auditDetail : x);
      });
      ctx.patchState({auditWithDetails: auditCopy});
    }

    @Action(Act.IsUnconfirmedBackCountNdcExists)
    isUnconfirmedBackCountNdcExists(ctx: StateContext<AuditsStateModel>, {locationID, drugID}: Act.IsUnconfirmedBackCountNdcExists): Observable<any> {
      ctx.patchState({loading: true, success: false});
      return this.http.get(endpoints.audit.isUnconfirmedBackCountNdcExists + '/' + locationID + '/' + drugID)
        .pipe(
          tap((resp: any) => {
            ctx.patchState({loading: false, response: resp});
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.BulkGetNdcsExistInUnconfirmedBackCount)
    bulkGetNdcsExistInUnconfirmedBackCount(ctx: StateContext<AuditsStateModel>, {drugsAndLocations}: Act.BulkGetNdcsExistInUnconfirmedBackCount): Observable<any> {
      ctx.patchState({loading: true, success: false, currentUnconfirmedNdcsInBackCount: []});

      return this.http.post(endpoints.audit.bulkGetNdcsExistInUnconfirmedBackCount, drugsAndLocations)
        .pipe(
          tap((resp: any) => {
            if (resp?.success) {
              ctx.patchState({currentUnconfirmedNdcsInBackCount: resp.value });
            }
            ctx.patchState({loading: false });
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetBackCountsInProgressCount)
    getBackCountsInProgressCount(ctx: StateContext<AuditsStateModel>, {}: Act.GetBackCountsInProgressCount): Observable<any> {
      ctx.patchState({loading: true, success: false});
      return this.http.get(endpoints.audit.getBackCountsInProgressCount)
        .pipe(
          tap((resp: any) => {
            ctx.patchState({loading: false, backCountsCount: resp?.value});
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.GetFirstBackCountID)
    getFirstBackCountID(ctx: StateContext<AuditsStateModel>, {}: Act.GetFirstBackCountID): Observable<any> {
      ctx.patchState({loading: true, success: false, firstBackCountID: null});
      return this.http.get(endpoints.audit.getFirstBackCountID)
        .pipe(
          tap((resp: any) => {
            ctx.patchState({loading: false, firstBackCountID: resp?.value});
          }),
          catchError((err) => {
            ctx.patchState({loading: false});
            return of(null);
          }),
        );
    }

    @Action(Act.DecrementBackCountsTotal)
    decrementBackCountsTotal(ctx: StateContext<AuditsStateModel>, {}: Act.DecrementBackCountsTotal): void {
      let count = ctx.getState().backCountsCount;
      if (count > 0) {
        ctx.patchState({backCountsCount: --count});
      }
    }

}
